// ─────────────────────────────────────────────────────────────────────────────
// PreScanPage.tsx
//
// Page 1 of the demo flow.  Pick reader + camera options before scanning.
//
// CONTROLS
//   Reader   — which WASM model to load
//   Lens     — Auto / Wide / Ultra-wide. On Android these usually map to
//              separate camera devices (matched by label hint). On iOS most
//              phones expose only one rear camera so the dropdown becomes
//              effectively a no-op there — that's expected.
//   Camera   — explicit device picker. Populated from
//              navigator.mediaDevices.enumerateDevices(). Browsers hide
//              labels until the user has granted camera permission at least
//              once for this origin, so on the very first visit the list
//              shows just "Default rear". Once the user has scanned once,
//              labels are remembered and the list fills out on the next
//              visit.
//   Torch    — boolean preference. Applied by ScanPage when the camera
//              starts; some devices/browsers don't support it and the
//              toggle has no effect there.
//
// We intentionally don't open a camera stream on this page — page 1 is
// configuration only. The user's choices are passed through to ScanPage,
// which owns the actual camera lifecycle.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Collapse,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import ClearIcon from '@mui/icons-material/Clear';
import { requestOrientationPermission, findCameraByLens } from '@novarumdx/ndx-imaging-web';

import { DEFAULT_HOUSINGS, type HousingConfig } from './readers';


export type LensPref = 'auto' | 'wide' | 'ultra';


export interface PreScanPageProps {
  /**
   * Full list of available cassette types to show in the dropdown.
   * In Phase 1 this comes from DEFAULT_HOUSINGS; in Phase 2 it will be fetched
   * from the backend API by ImagingTest and passed down here.
   * Defaults to DEFAULT_HOUSINGS if omitted (useful in tests / Storybook).
   */
  housings?: HousingConfig[];
  /** The currently selected cassette type. */
  housing: HousingConfig;
  onHousingChange: (h: HousingConfig) => void;

  /** True once the WASM engine has finished loading the current model. */
  engineReady: boolean;
  /** A cassette configuration or SDK initialisation failure to show to the user. */
  errorMessage?: string | null;

  /** Called when the user clicks "Start scanning". */
  onStart: () => void;
  /** Enables SDK orientation capture after browser permission is granted. */
  onEnableOrientation: () => void;

  /** Persisted camera device ID (empty string = default rear camera). */
  cameraDeviceId: string;
  onCameraDeviceIdChange: (id: string) => void;

  /** Persisted torch preference — applied by ScanPage when the stream starts. */
  torchEnabled: boolean;
  onTorchChange: (on: boolean) => void;

  /** Optional homography scale override. undefined = use engine default. */
  hScale: number | undefined;
  onHScaleChange: (value: number | undefined) => void;
  /** Number of frames to collect per strip before analysis. */
  framesToCapture: number;
  onFramesToCaptureChange: (v: number) => void;
  /** When true, strip analysis is skipped — only images and homographies returned. */
  collectionMode: boolean;
  onCollectionModeChange: (v: boolean) => void;
  // visualizationMode is intentionally absent: NDXReaderView reads it directly
  // from scanner.visualizationMode (set by the worker), so a UI override here
  // has no effect on rendering.
}



const PreScanPage: React.FC<PreScanPageProps> = ({
  housings = DEFAULT_HOUSINGS,
  housing,
  onHousingChange,
  engineReady,
  errorMessage,
  onStart,
  onEnableOrientation,
  cameraDeviceId,
  onCameraDeviceIdChange,
  torchEnabled,
  onTorchChange,
  hScale,
  onHScaleChange,
  framesToCapture,
  onFramesToCaptureChange,
  collectionMode,
  onCollectionModeChange,
}) => {

  // ── Local UI state ─────────────────────────────────────────────────────────
  const [lens, setLens] = useState<LensPref>('auto');
  const [cameras, setCameras] = useState<MediaDeviceInfo[]>([]);
  const [showAdvanced, setShowAdvanced] = useState(false);

  // H-factor input: track the raw string the user is typing independently of
  // the validated hScale prop. Without this, typing "0" or "0." causes the
  // input to snap back because parseFloat("0") fails the >= 0.5 range check,
  // so onHScaleChange never fires and value={hScale ?? ''} reverts the display.
  const [hInput, setHInput] = useState<string>(hScale !== undefined ? String(hScale) : '');

  // Keep local string in sync when the parent clears hScale (e.g. X button)
  // or when testConfig pre-populates it on housing change.
  useEffect(() => {
    setHInput(hScale !== undefined ? String(hScale) : '');
  }, [hScale]);

  // Frames-to-capture input: same raw-string tracking pattern as hInput.
  const [framesInput, setFramesInput] = useState<string>(String(framesToCapture));

  // Keep local string in sync when parent updates framesToCapture (e.g. on housing change).
  useEffect(() => {
    setFramesInput(String(framesToCapture));
  }, [framesToCapture]);


  // ── Enumerate cameras on mount ─────────────────────────────────────────────
  // No permission prompt and no preview — labels populate only after the
  // user has granted camera access at least once via the scan page.
  useEffect(() => {
    let cancelled = false;
    navigator.mediaDevices?.enumerateDevices()
      .then((devices) => {
        if (cancelled) return;
        setCameras(devices.filter((d) => d.kind === 'videoinput'));
      })
      .catch((e) => console.warn('[PreScanPage] enumerateDevices:', e));
    return () => { cancelled = true; };
  }, []);


  // ── Lens picker ────────────────────────────────────────────────────────────
  // Picking a lens is a shorthand for picking a specific camera device when
  // the OS exposes them separately (typical on Android Chrome). On iOS most
  // phones expose just one rear camera so this falls through to a no-op.
  const handleLensChange = (next: LensPref) => {
    setLens(next);

    if (next === 'auto') {
      onCameraDeviceIdChange('');
      return;
    }
    // 'wide' | 'ultra' → resolve the matching device via the SDK helper.
    // On null (e.g. iOS single rear camera, or empty device list) fall back to
    // the default camera by clearing the device ID, so the UI and stream stay in
    // sync rather than retaining a previously-selected device.
    const id = findCameraByLens(cameras, next);
    onCameraDeviceIdChange(id ?? '');
  };

  const handleStart = async () => {
    const orientationAllowed = await requestOrientationPermission();
    if (orientationAllowed) onEnableOrientation();
    onStart();
  };


  // ── Render ─────────────────────────────────────────────────────────────────
  // Outer Box owns the scroll because index.css sets `html, body { overflow:
  // hidden }` for the rest of the portal sample.
  //
  // Prefer 100dvh (dynamic viewport) so iOS Safari doesn't hide the bottom
  // of the page behind the URL bar; fall back to 100vh on older browsers
  // that don't support the dvh unit yet.
  return (
    <Box sx={{
      height: '100vh',
      '@supports (height: 100dvh)': { height: '100dvh' },
      overflowY: 'auto',
      overflowX: 'hidden',
      WebkitOverflowScrolling: 'touch',
      bgcolor: 'grey.50',
    }}>
      <Box sx={{ maxWidth: 540, mx: 'auto', px: 2.5, pt: 4, pb: 8 }}>

        {/* ── Device / browser compatibility warnings ───────────────────── */}
        {/Firefox\//.test(navigator.userAgent) && (
          <Alert severity="info" sx={{ mb: 2, borderRadius: 1.5 }}>
            Firefox has limited camera support on some devices. For best results
            use Chrome (Android) or Safari (iOS).
          </Alert>
        )}

        {/* ── Brand header ──────────────────────────────────────────────── */}
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="overline"
            sx={{ color: 'primary.main', fontWeight: 700, letterSpacing: 2, lineHeight: 1 }}
          >
            NovarumDX
          </Typography>
          <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mt: 0.5 }}>
            <Typography variant="h5" fontWeight={600}>
              Reader Demo
            </Typography>
            {/* Inline engine status — compact so it doesn't push content down */}
            <Stack direction="row" alignItems="center" gap={0.75}>
              {errorMessage
                ? <Box component="span" sx={{ color: 'error.main', fontSize: 14, lineHeight: 1 }}>!</Box>
                : !engineReady
                ? <CircularProgress size={13} />
                : <Box component="span" sx={{ color: '#2e7d32', fontSize: 14, lineHeight: 1 }}>✓</Box>
              }
              <Typography
                variant="caption"
                fontWeight={500}
                sx={{ color: errorMessage ? 'error.main' : engineReady ? '#2e7d32' : 'text.secondary' }}
              >
                {errorMessage ? 'Unavailable' : engineReady ? 'Ready' : 'Loading…'}
              </Typography>
            </Stack>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Select the cassette type and camera options below, then tap{' '}
            <strong>Begin Scan</strong> to start image capture.
          </Typography>
        </Box>

        {errorMessage && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 1.5 }}>
            {errorMessage} Select another cassette type or reload the page.
          </Alert>
        )}

        {/* ── Cassette type ──────────────────────────────────────────────── */}
        <Card
          elevation={0}
          sx={{ mb: 2, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}
        >
          <CardContent>
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                mb: 1.5,
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: 0.8,
                color: 'text.secondary',
              }}
            >
              Cassette Type
            </Typography>
            <FormControl size="small" fullWidth>
              <InputLabel id="cassette-type-label">Cassette Type</InputLabel>
              <Select
                labelId="cassette-type-label"
                value={housing.id}
                label="Cassette Type"
                // Show a compact image + label in the closed (selected) state.
                renderValue={(id) => {
                  const h = housings.find((hh) => hh.id === id);
                  if (!h) return String(id);
                  if (!h.imageUrl) return h.label;
                  return (
                    <Stack direction="row" alignItems="center" gap={1}>
                      <Box
                        component="img"
                        src={h.imageUrl}
                        alt=""
                        sx={{ width: 28, height: 28, objectFit: 'contain', borderRadius: 0.5 }}
                      />
                      <span>{h.label}</span>
                    </Stack>
                  );
                }}
                onChange={(e) => {
                  const selected = housings.find((h) => h.id === e.target.value);
                  if (selected) onHousingChange(selected);
                }}
              >
                {housings.map((h) => (
                  <MenuItem key={h.id} value={h.id}>
                    <Stack direction="row" alignItems="center" gap={1.5}>
                      {h.imageUrl && (
                        <Box
                          component="img"
                          src={h.imageUrl}
                          alt=""
                          sx={{
                            width: 44,
                            height: 44,
                            objectFit: 'contain',
                            borderRadius: 1,
                            flexShrink: 0,
                            bgcolor: 'grey.100',
                            p: 0.25,
                          }}
                        />
                      )}
                      <Typography variant="body2">{h.label}</Typography>
                    </Stack>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </CardContent>
        </Card>

        {/* ── Camera settings ────────────────────────────────────────────── */}
        <Card
          elevation={0}
          sx={{ mb: 3, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}
        >
          <CardContent>
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                mb: 1.5,
                fontWeight: 600,
                textTransform: 'uppercase',
                letterSpacing: 0.8,
                color: 'text.secondary',
              }}
            >
              Camera Settings
            </Typography>
            <Stack spacing={1.5}>
              <Stack direction="row" spacing={1.5}>
                <FormControl size="small" fullWidth>
                  <InputLabel>Lens</InputLabel>
                  <Select
                    value={lens}
                    label="Lens"
                    onChange={(e) => handleLensChange(e.target.value as LensPref)}
                  >
                    <MenuItem value="auto">Auto</MenuItem>
                    <MenuItem value="wide">Wide (1x)</MenuItem>
                    <MenuItem value="ultra">Ultra-wide (0.5x)</MenuItem>
                  </Select>
                </FormControl>

                <FormControl size="small" fullWidth>
                  <InputLabel>Device</InputLabel>
                  <Select
                    value={cameraDeviceId}
                    label="Device"
                    onChange={(e) => onCameraDeviceIdChange(e.target.value)}
                  >
                    <MenuItem value="">Default rear</MenuItem>
                    {cameras.map((c) => (
                      <MenuItem key={c.deviceId} value={c.deviceId}>
                        {c.label || `Camera ${c.deviceId.slice(0, 6)}`}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Stack>

              <Divider />

              <FormControlLabel
                sx={{ mx: 0 }}
                control={
                  <Switch
                    size="small"
                    checked={torchEnabled}
                    onChange={(_, on) => onTorchChange(on)}
                  />
                }
                label={
                  <Box sx={{ ml: 0.5 }}>
                    <Typography variant="body2">Torch / Flash</Typography>
                    <Typography variant="caption" color="text.secondary">
                      Support varies by device and browser
                    </Typography>
                  </Box>
                }
              />

            </Stack>
          </CardContent>
        </Card>

        {/* ── Advanced settings ─────────────────────────────────────────── */}
        <Box sx={{ mb: 3 }}>
          <Button
            size="small"
            variant="text"
            onClick={() => setShowAdvanced((v) => !v)}
            sx={{ px: 1, color: 'text.secondary', fontSize: '0.8rem', textTransform: 'none' }}
          >
            {showAdvanced ? '▲ Hide advanced settings' : '▼ Advanced settings'}
          </Button>
          <Collapse in={showAdvanced} unmountOnExit>
            <Card
              elevation={0}
              sx={{ mt: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}
            >
              <CardContent>
                <Typography
                  variant="caption"
                  sx={{
                    display: 'block',
                    mb: 1.5,
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: 0.8,
                    color: 'text.secondary',
                  }}
                >
                  Advanced Settings
                </Typography>
                <TextField
                  size="small"
                  label="H Factor"
                  type="number"
                  value={hInput}
                  placeholder="Engine default"
                  onChange={(e) => {
                    const raw = e.target.value;
                    setHInput(raw);
                    if (raw === '') { onHScaleChange(undefined); return; }
                    const v = parseFloat(raw);
                    // Only commit to parent once we have a complete, in-range number.
                    // hInput continues to track the raw string so intermediate states
                    // like "0", "0.", "0.9" display correctly while typing.
                    if (!isNaN(v) && v >= 0.5 && v <= 3.0) onHScaleChange(v);
                  }}
                  inputProps={{ step: 0.1, min: 0.5, max: 3.0 }}
                  InputProps={{
                    endAdornment: hInput !== '' && (
                      <InputAdornment position="end">
                        <IconButton
                          size="small"
                          onClick={() => { onHScaleChange(undefined); setHInput(''); }}
                          edge="end"
                          aria-label="Clear H Factor"
                        >
                          <ClearIcon sx={{ fontSize: 16 }} />
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{ width: 160 }}
                />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                  Controls how the engine searches for the cassette (range 0.5–3.0).{' '}
                  <strong>Lower values</strong> are faster but only find cassettes that fill most of the frame.{' '}
                  <strong>Higher values</strong> search a wider scale range — needed for laptop or
                  desktop cameras, which are wide-angle and sit further from the cassette.
                  Try <strong>1.5–2.0</strong> if scanning on a laptop or webcam.
                  Leave blank to use the engine default (works well for most mobile cameras).
                  Changing this value reloads the engine.
                </Typography>

                <Box sx={{ mt: 2 }}>
                  <TextField
                    size="small"
                    label="Frames to Capture"
                    type="number"
                    value={framesInput}
                    onChange={(e) => {
                      const raw = e.target.value;
                      setFramesInput(raw);
                      const v = parseInt(raw, 10);
                      if (!isNaN(v) && v >= 1 && v <= 50) onFramesToCaptureChange(v);
                    }}
                    inputProps={{ step: 1, min: 1, max: 50 }}
                    sx={{ width: 160 }}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                    Number of frames collected per strip before analysis. Higher values are more
                    accurate but slower. The cassette value is shown initially; changing it explicitly
                    overrides the cassette for this reader.
                  </Typography>
                </Box>

                <Box sx={{ mt: 2 }}>
                  <FormControlLabel
                    sx={{ mx: 0 }}
                    control={
                      <Switch
                        size="small"
                        checked={collectionMode}
                        onChange={(_, on) => onCollectionModeChange(on)}
                      />
                    }
                    label={
                      <Box sx={{ ml: 0.5 }}>
                        <Typography variant="body2">Collection Mode</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Disables strip analysis — returns images and homographies only. The cassette
                          value is shown initially; changing it explicitly overrides the cassette.
                        </Typography>
                      </Box>
                    }
                  />
                </Box>

              </CardContent>
            </Card>
          </Collapse>
        </Box>

        {/* ── Start button ───────────────────────────────────────────────── */}
        <Button
          variant="contained"
          size="large"
          fullWidth
          disabled={!engineReady}
          onClick={handleStart}
          sx={{ py: 1.5, borderRadius: 1.5, fontSize: '1rem', fontWeight: 600 }}
        >
          {engineReady ? 'Begin Scan' : errorMessage ? 'Cassette unavailable' : 'Loading…'}
        </Button>

        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', textAlign: 'center', mt: 2 }}
        >
          For demonstration purposes only · Not for clinical use
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', textAlign: 'center', mt: 0.5 }}
        >
          SDK ndx-imaging-web v{__NDX_SDK_VERSION__}
        </Typography>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: 'block', textAlign: 'center', mt: 0.5 }}
        >
          <Link href="https://www.novarumdx.com/" target="_blank" rel="noopener noreferrer">
            novarumdx.com
          </Link>
        </Typography>

      </Box>
    </Box>
  );
};

export default PreScanPage;
